import { useCallback, useEffect, useId, useMemo, useState } from 'react';
import { useWillLeave } from '../router/Router';
import { globalFocus } from 't-lib-ui/helpers';
const queryCache = new Map(); // hierarchical cache
let maxCacheSize = 100;
const MAX_CACHE_PURGE_PCT = 20;
export function setMaxCacheSize(size) {
    maxCacheSize = size;
}
export const isPromise = (v) => typeof v === 'object' && typeof v.then === 'function';
export function clearCache(clearLocalStorage = true) {
    queryCache.clear();
    if (clearLocalStorage) {
        clearLocalStorageCache();
    }
}
////// LOCALSTORAGE CACHE
const LOCAL_STORAGE_PREFIX = 'qc-';
function getLocalStorageKey(keys) {
    return LOCAL_STORAGE_PREFIX + JSON.stringify(keys);
}
function clearLocalStorageCache() {
    try {
        const keysToRemove = [];
        for (let i = 0; i < localStorage.length; i++) {
            const key = localStorage.key(i);
            if (key === null || key === void 0 ? void 0 : key.startsWith(LOCAL_STORAGE_PREFIX)) {
                keysToRemove.push(key);
            }
        }
        keysToRemove.forEach(key => localStorage.removeItem(key));
    }
    catch (e) {
        console.warn('Failed to clear localStorage cache:', e);
    }
}
function getFromLocalStorageCache(keys, maxAgeMs) {
    try {
        const lsKey = getLocalStorageKey(keys);
        const stored = localStorage.getItem(lsKey);
        if (!stored)
            return undefined;
        const entry = JSON.parse(stored);
        const now = Date.now();
        if (now - entry.lastUpdate > (maxAgeMs !== null && maxAgeMs !== void 0 ? maxAgeMs : entry.maxAgeMs)) {
            localStorage.removeItem(lsKey);
            return undefined;
        }
        return entry;
    }
    catch (e) {
        console.warn('Failed to read from localStorage cache:', e);
        return undefined;
    }
}
function putIntoLocalStorageCache(keys, entry) {
    try {
        const lsKey = getLocalStorageKey(keys);
        localStorage.setItem(lsKey, JSON.stringify(entry));
    }
    catch (e) {
        console.warn('Failed to write to localStorage cache:', e);
    }
}
function removeFromLocalStorageCache(keys) {
    try {
        const keyPrefix = JSON.stringify(keys);
        const keysToRemove = [];
        for (let i = 0; i < localStorage.length; i++) {
            const lsKey = localStorage.key(i);
            if (!(lsKey === null || lsKey === void 0 ? void 0 : lsKey.startsWith(LOCAL_STORAGE_PREFIX)))
                continue;
            const storedKeyJson = lsKey.slice(LOCAL_STORAGE_PREFIX.length);
            // Check if stored key starts with the prefix (for subtree deletion)
            // e.g., keys=['foo'] should delete ['foo'], ['foo','bar'], ['foo','bar','baz']
            try {
                const storedKeys = JSON.parse(storedKeyJson);
                if (storedKeys.length >= keys.length) {
                    let isMatch = true;
                    for (let j = 0; j < keys.length; j++) {
                        if (storedKeys[j] !== keys[j]) {
                            isMatch = false;
                            break;
                        }
                    }
                    if (isMatch) {
                        keysToRemove.push(lsKey);
                    }
                }
            }
            catch {
                // Invalid JSON, skip
            }
        }
        keysToRemove.forEach(key => localStorage.removeItem(key));
    }
    catch (e) {
        console.warn('Failed to remove from localStorage cache:', e);
    }
}
function _getFromQueryCache(keys, index, base, maxAgeMs) {
    if (index >= keys.length)
        return undefined;
    const key = keys[index];
    const entry = base.get(key);
    if (!entry)
        return undefined;
    if (isPromise(entry))
        return entry;
    if (entry instanceof Map)
        return _getFromQueryCache(keys, index + 1, entry, maxAgeMs);
    const e = entry;
    if (Date.now() - e.lastUpdate > (maxAgeMs !== null && maxAgeMs !== void 0 ? maxAgeMs : e.maxAgeMs)) {
        base.delete(key);
        return undefined;
    }
    return e;
}
export function getFromQueryCache(keys, maxAgeMs, useLocalStorage = false) {
    const memoryResult = _getFromQueryCache(keys, 0, queryCache, maxAgeMs);
    if (memoryResult)
        return memoryResult;
    // Fall back to localStorage if enabled and not found in memory
    if (useLocalStorage) {
        const lsEntry = getFromLocalStorageCache(keys, maxAgeMs);
        if (lsEntry) {
            // Hydrate into memory cache for subsequent accesses
            _putIntoQueryCache(keys, 0, queryCache, lsEntry.data, lsEntry.maxAgeMs);
            return lsEntry;
        }
    }
    return undefined;
}
function convertEntryToArray(entry) {
    return [entry.keys, entry.data];
}
function isEntryValid(entry, now, maxAgeMs) {
    return !isPromise(entry) && (now - entry.lastUpdate <= (maxAgeMs !== null && maxAgeMs !== void 0 ? maxAgeMs : entry.maxAgeMs));
}
function _getAllCacheEntries(keys, index, base, maxAgeMs) {
    if (index >= keys.length)
        return [];
    if (index == keys.length - 1) {
        const c = base.get(keys[index]);
        if (!c)
            return [];
        const now = Date.now();
        return (c instanceof Map) ? Array.from(c.values())
            .filter(it => (!(it instanceof Map)) && isEntryValid(it, now))
            .map(convertEntryToArray) : (isEntryValid(c, now, maxAgeMs) ? [convertEntryToArray(c)] : []);
    }
    const e = base.get(keys[index]);
    if (!e || !(e instanceof Map))
        return [];
    const result = [];
    const now = Date.now();
    for (const e2 of Array.from(e.values()))
        if (e2 instanceof Map)
            result.push(..._getAllCacheEntries(keys, index + 1, e2, maxAgeMs));
        else if (!isPromise(e2))
            if (isEntryValid(e2, now, maxAgeMs))
                result.push(convertEntryToArray(e2));
    return result;
}
export function getAllCacheEntries(keys) {
    return _getAllCacheEntries(keys, 0, queryCache);
}
function _storeEntryInQueryCache(keys, cache, key, entry, maxAgeMs) {
    if (cache.get(key) instanceof Map)
        throw new Error('Invalid cache state. You must not store a value in a cache that has a sub-cache with the same key.');
    if (cache.size >= maxCacheSize) {
        const keys = Array.from(cache.keys());
        const rndStart = Math.floor(Math.random() * keys.length);
        let removed = 0;
        const now = Date.now();
        const cachePurgeSize = Math.ceil(Math.max(keys.length * MAX_CACHE_PURGE_PCT / 100, 1));
        for (let i = 0; i < cachePurgeSize; i++) {
            const e = cache.get(keys[(rndStart + i) % keys.length]);
            if (e instanceof Map || isPromise(e) || (e.lastUpdate + (maxAgeMs !== null && maxAgeMs !== void 0 ? maxAgeMs : e.maxAgeMs)) < now) {
                cache.delete(keys[(rndStart + i) % keys.length]);
                removed++;
            }
        }
        if (!removed)
            cache.delete(keys[rndStart]);
    }
    cache.set(key, entry);
}
function _storeInQueryCache(keys, cache, key, data, maxAgeMs) {
    _storeEntryInQueryCache(keys, cache, key, { keys, data, lastUpdate: Date.now(), maxAgeMs }, maxAgeMs);
    return data;
}
function _putIntoQueryCache(keys, index, base, data, maxAgeMs) {
    const key = keys[index];
    if (index >= keys.length - 1)
        return _storeInQueryCache(keys, base, key, data, maxAgeMs);
    const e = base.get(key);
    if (!e) {
        const newCache = new Map();
        base.set(key, newCache);
        return _putIntoQueryCache(keys, index + 1, newCache, data, maxAgeMs);
    }
    if (e instanceof Map)
        return _putIntoQueryCache(keys, index + 1, e, data, maxAgeMs);
    else
        throw new Error('Invalid cache state. You must not store a value in a cache that has a sub-cache with the same key.');
}
export function putIntoQueryCache(keys, data, maxAgeMs, useLocalStorage = false) {
    const result = _putIntoQueryCache(keys, 0, queryCache, data, maxAgeMs);
    if (useLocalStorage) {
        putIntoLocalStorageCache(keys, { keys, data, lastUpdate: Date.now(), maxAgeMs });
    }
    return result;
}
function _putEntryIntoQueryCache(keys, index, base, entry, maxAgeMs) {
    const key = keys[index];
    if (index >= keys.length - 1)
        return _storeEntryInQueryCache(keys, base, key, entry, maxAgeMs);
    const e = base.get(key);
    if (!e) {
        const newCache = new Map();
        base.set(key, newCache);
        return _putEntryIntoQueryCache(keys, index + 1, newCache, entry, maxAgeMs);
    }
    if (e instanceof Map)
        return _putEntryIntoQueryCache(keys, index + 1, e, entry, maxAgeMs);
    else
        throw new Error('Invalid cache state. You must not store a value in a cache that has a sub-cache with the same key.');
}
export function putEntryIntoQueryCache(keys, entry, maxAgeMs, useLocalStorage = false) {
    _putEntryIntoQueryCache(keys, 0, queryCache, entry, maxAgeMs);
    if (useLocalStorage) {
        putIntoLocalStorageCache(keys, entry);
    }
}
function _putPromiseIntoQueryCache(keys, index, base, futureResult) {
    const key = keys[index];
    if (index >= keys.length - 1) {
        base.set(key, futureResult);
        return;
    }
    const e = base.get(key);
    if (!e) {
        const newCache = new Map();
        base.set(key, newCache);
        _putPromiseIntoQueryCache(keys, index + 1, newCache, futureResult);
    }
    else if (e instanceof Map)
        _putPromiseIntoQueryCache(keys, index + 1, e, futureResult);
    else
        throw new Error('Invalid cache state. You must not store any value in a cache that has a sub-cache with the same key.');
}
export function putPromiseIntoQueryCache(keys, futureResult, maxAgeMs, useLocalStorage = false) {
    _putPromiseIntoQueryCache(keys, 0, queryCache, futureResult);
    // For localStorage: capture existing lastUpdate, persist on fulfillment if unchanged
    if (useLocalStorage && maxAgeMs) {
        const existingEntry = getFromLocalStorageCache(keys);
        const existingLastUpdate = existingEntry === null || existingEntry === void 0 ? void 0 : existingEntry.lastUpdate;
        futureResult.then((data) => {
            // Check if another write happened in the meantime
            const currentEntry = getFromLocalStorageCache(keys);
            const currentLastUpdate = currentEntry === null || currentEntry === void 0 ? void 0 : currentEntry.lastUpdate;
            // Only persist if no newer write occurred
            if (existingLastUpdate === currentLastUpdate) {
                putIntoLocalStorageCache(keys, { keys, data, lastUpdate: Date.now(), maxAgeMs });
            }
        }).catch(() => {
            // Don't persist on error
        });
    }
}
function _removeFromQueryCache(keys, index, base) {
    if (index < keys.length - 1) {
        // Navigate deeper into the hierarchy - we haven't reached the target key yet
        const c = base.get(keys[index]);
        if (!c || !(c instanceof Map))
            return;
        return _removeFromQueryCache(keys, index + 1, c);
    }
    // We've reached the target key - delete it and all its children
    // WARNING: This deletes the entire subtree! E.g., deleting ['foo'] also deletes ['foo', 'bar']
    base.delete(keys[index]);
}
export function removeFromQueryCache(keys, useLocalStorage = false) {
    _removeFromQueryCache(keys, 0, queryCache);
    if (useLocalStorage) {
        removeFromLocalStorageCache(keys);
    }
}
/// ------ Basic Query -------- 
const DEFAULT_CACHING_MS = 10000;
export var QueryStatus;
(function (QueryStatus) {
    QueryStatus[QueryStatus["Pending"] = 0] = "Pending";
    QueryStatus[QueryStatus["Loading"] = 1] = "Loading";
    QueryStatus[QueryStatus["Success"] = 2] = "Success";
    QueryStatus[QueryStatus["Error"] = 3] = "Error";
    QueryStatus[QueryStatus["Disabled"] = 4] = "Disabled";
})(QueryStatus || (QueryStatus = {}));
export function useQuery(keyInit, inputInit, fetcher, options) {
    var _a;
    const [status, setStatus] = useState(QueryStatus.Pending);
    const [data, setData] = useState();
    const [error, setError] = useState();
    const [timestamp, setTimestamp] = useState(Date.now());
    const [inputData, setInputData] = useState(inputInit);
    const [key, setKey] = useState(keyInit instanceof Function ? keyInit(inputInit) : keyInit);
    const enabled = (_a = options === null || options === void 0 ? void 0 : options.enabled) !== null && _a !== void 0 ? _a : true;
    async function refresh(force = false) {
        var _a, _b, _c, _d;
        if (!enabled)
            return;
        if (!force) {
            const c = getFromQueryCache(key, (_a = options === null || options === void 0 ? void 0 : options.maxReadAgeMs) !== null && _a !== void 0 ? _a : options === null || options === void 0 ? void 0 : options.maxAgeMs, options === null || options === void 0 ? void 0 : options.useLocalStorage);
            if (isPromise(c)) {
                setStatus(QueryStatus.Loading);
                return c.then((data) => {
                    setData(data);
                    setStatus(QueryStatus.Success);
                    setTimestamp(Date.now());
                }).catch((error) => {
                    setError(error);
                    setStatus(QueryStatus.Error);
                });
            }
            if (c && (!(options === null || options === void 0 ? void 0 : options.cacheValidator) || options.cacheValidator(c.data))) {
                setData((options === null || options === void 0 ? void 0 : options.cacheProcessor) ? options.cacheProcessor(c.data) : c.data);
                setStatus(QueryStatus.Success);
                return;
            }
        }
        setStatus(QueryStatus.Loading);
        (_c = (_b = options === null || options === void 0 ? void 0 : options.onOperation) === null || _b === void 0 ? void 0 : _b[0]) === null || _c === void 0 ? void 0 : _c.call(_b);
        const p = fetcher(inputData).then((data) => {
            var _a, _b, _c, _d;
            putIntoQueryCache(key, data, (_a = options === null || options === void 0 ? void 0 : options.maxAgeMs) !== null && _a !== void 0 ? _a : DEFAULT_CACHING_MS, options === null || options === void 0 ? void 0 : options.useLocalStorage);
            // Extract additional cache entries if cacheExtractor is provided
            if (options === null || options === void 0 ? void 0 : options.cacheExtractor) {
                const extraCacheEntries = options.cacheExtractor(data);
                extraCacheEntries.forEach(entry => {
                    var _a, _b;
                    putIntoQueryCache(entry.key, entry.value, (_b = (_a = entry.maxAgeMs) !== null && _a !== void 0 ? _a : options === null || options === void 0 ? void 0 : options.maxAgeMs) !== null && _b !== void 0 ? _b : DEFAULT_CACHING_MS, options === null || options === void 0 ? void 0 : options.useLocalStorage);
                });
            }
            setData(data);
            setStatus(QueryStatus.Success);
            setTimestamp(Date.now());
            (_c = (_b = options === null || options === void 0 ? void 0 : options.onOperation) === null || _b === void 0 ? void 0 : _b[1]) === null || _c === void 0 ? void 0 : _c.call(_b, true);
            (_d = options === null || options === void 0 ? void 0 : options.onSuccess) === null || _d === void 0 ? void 0 : _d.call(options, data);
            return data;
        }).catch((error) => {
            var _a, _b, _c;
            (_b = (_a = options === null || options === void 0 ? void 0 : options.onOperation) === null || _a === void 0 ? void 0 : _a[1]) === null || _b === void 0 ? void 0 : _b.call(_a, false);
            removeFromQueryCache(key, options === null || options === void 0 ? void 0 : options.useLocalStorage);
            setError(error);
            setStatus(QueryStatus.Error);
            (_c = options === null || options === void 0 ? void 0 : options.onError) === null || _c === void 0 ? void 0 : _c.call(options, error);
            return Promise.reject(error);
        });
        putPromiseIntoQueryCache(key, p, (_d = options === null || options === void 0 ? void 0 : options.maxAgeMs) !== null && _d !== void 0 ? _d : DEFAULT_CACHING_MS, options === null || options === void 0 ? void 0 : options.useLocalStorage);
        await p.catch(() => { });
    }
    function changeInput(input) {
        if (keyInit instanceof Function)
            setKey(keyInit(input));
        setInputData(input);
    }
    useEffect(() => { if (enabled)
        refresh(); }, [key.join('-'), inputData, enabled]); // the join is important to create a stable key! Otherwise, the key would be a new array every time.
    if ((options === null || options === void 0 ? void 0 : options.enabled) === false)
        return { status: QueryStatus.Disabled, data: undefined, error: undefined, refresh, timestamp, changeInput };
    return { status, data, error, refresh, timestamp, changeInput };
}
export function useConvertingQuery(initKey, initialInput, fetcher, converter, options) {
    if (options === null || options === void 0 ? void 0 : options.cacheProcessor)
        throw new Error('cacheProcessor is not allowed in useConvertingQuery');
    const convertedOptions = {
        ...options,
        onSuccess: (options === null || options === void 0 ? void 0 : options.onSuccess) && ((data) => options.onSuccess(converter(data))),
        cacheProcessor: undefined,
        cacheValidator: (options === null || options === void 0 ? void 0 : options.cacheValidator) && ((data) => { var _a; return ((_a = options === null || options === void 0 ? void 0 : options.cacheValidator) === null || _a === void 0 ? void 0 : _a.call(options, converter(data))) || false; }),
        cacheExtractor: (options === null || options === void 0 ? void 0 : options.cacheExtractor) && ((data) => options.cacheExtractor(converter(data))),
    };
    const result = useQuery(initKey, initialInput, fetcher, convertedOptions);
    return useMemo(() => {
        return {
            status: result.status,
            data: result.data ? converter(result.data) : undefined,
            error: result.error,
            refresh: result.refresh,
            timestamp: result.timestamp,
            changeInput: result.changeInput
        };
    }, [result.data, result.status, result.error, result.timestamp]);
}
export function createDummyQuery(data) {
    return { status: QueryStatus.Success, data, refresh: () => Promise.resolve(), timestamp: 0, changeInput: () => { } };
}
export function useListQuery(chunkSize, queryInit, keyProducer, fetcher, options) {
    var _a;
    const [status, setStatus] = useState(QueryStatus.Pending);
    const [data, setData] = useState({ chunks: [], hasMore: true });
    const [error, setError] = useState();
    const [inputData, setInputData] = useState(queryInit);
    const enabled = (_a = options === null || options === void 0 ? void 0 : options.enabled) !== null && _a !== void 0 ? _a : true;
    const handleNewChunk = useCallback((chunk, append = true) => {
        if (append)
            setData(d => ({ ...d, chunks: [...d.chunks, chunk.chunk], hasMore: chunk.hasMore, cursor: chunk.cursor }));
        else
            setData({ chunks: [chunk.chunk], hasMore: chunk.hasMore, cursor: chunk.cursor });
        setStatus(QueryStatus.Success);
    }, []);
    const loadChunk = useCallback(async (inputData, last, force = false) => {
        var _a, _b, _c, _d;
        setInputData(inputData);
        if (!enabled)
            return;
        const key = keyProducer(inputData, chunkSize, last !== null && last !== void 0 ? last : '');
        if (!force) {
            const c = getFromQueryCache(key, (_a = options === null || options === void 0 ? void 0 : options.maxReadAgeMs) !== null && _a !== void 0 ? _a : options === null || options === void 0 ? void 0 : options.maxAgeMs, options === null || options === void 0 ? void 0 : options.useLocalStorage);
            if (isPromise(c)) {
                if (!last)
                    setData({ chunks: [], hasMore: false, cursor: undefined });
                setStatus(QueryStatus.Loading);
                return c.then((data) => {
                    handleNewChunk(data, !!last);
                    return data;
                }).catch((error) => {
                    setError(error);
                    setStatus(QueryStatus.Error);
                    return Promise.reject(error);
                });
            }
            if (c && (!(options === null || options === void 0 ? void 0 : options.cacheValidator) || options.cacheValidator(c.data.chunk))) {
                if (options === null || options === void 0 ? void 0 : options.cacheProcessor) {
                    const chunk = options.cacheProcessor(c.data.chunk);
                    const r = { ...c.data, chunk };
                    handleNewChunk(r, !!last);
                    return r;
                }
                else {
                    handleNewChunk(c.data, !!last);
                    return c.data;
                }
            }
        }
        setStatus(QueryStatus.Loading);
        if (!last)
            setData({ chunks: [], hasMore: false, cursor: undefined });
        (_c = (_b = options === null || options === void 0 ? void 0 : options.onOperation) === null || _b === void 0 ? void 0 : _b[0]) === null || _c === void 0 ? void 0 : _c.call(_b);
        const p = fetcher(inputData, chunkSize, last).then((data) => {
            var _a, _b, _c, _d;
            putIntoQueryCache(key, data, (_a = options === null || options === void 0 ? void 0 : options.maxAgeMs) !== null && _a !== void 0 ? _a : DEFAULT_CACHING_MS, options === null || options === void 0 ? void 0 : options.useLocalStorage);
            // Extract additional cache entries if cacheExtractor is provided
            if (options === null || options === void 0 ? void 0 : options.cacheExtractor) {
                const extraCacheEntries = options.cacheExtractor(data.chunk);
                extraCacheEntries.forEach(entry => {
                    var _a, _b;
                    putIntoQueryCache(entry.key, entry.value, (_b = (_a = entry.maxAgeMs) !== null && _a !== void 0 ? _a : options === null || options === void 0 ? void 0 : options.maxAgeMs) !== null && _b !== void 0 ? _b : DEFAULT_CACHING_MS, options === null || options === void 0 ? void 0 : options.useLocalStorage);
                });
            }
            handleNewChunk(data, !!last);
            (_c = (_b = options === null || options === void 0 ? void 0 : options.onOperation) === null || _b === void 0 ? void 0 : _b[1]) === null || _c === void 0 ? void 0 : _c.call(_b, true);
            (_d = options === null || options === void 0 ? void 0 : options.onSuccess) === null || _d === void 0 ? void 0 : _d.call(options, data.chunk);
            return data;
        }).catch((error) => {
            var _a, _b, _c;
            (_b = (_a = options === null || options === void 0 ? void 0 : options.onOperation) === null || _a === void 0 ? void 0 : _a[1]) === null || _b === void 0 ? void 0 : _b.call(_a, false);
            removeFromQueryCache(key, options === null || options === void 0 ? void 0 : options.useLocalStorage);
            setError(error);
            setStatus(QueryStatus.Error);
            (_c = options === null || options === void 0 ? void 0 : options.onError) === null || _c === void 0 ? void 0 : _c.call(options, error);
            return Promise.reject(error);
        });
        putPromiseIntoQueryCache(key, p, (_d = options === null || options === void 0 ? void 0 : options.maxAgeMs) !== null && _d !== void 0 ? _d : DEFAULT_CACHING_MS, options === null || options === void 0 ? void 0 : options.useLocalStorage);
        await p.catch(() => { });
        return p;
    }, [enabled, chunkSize, keyProducer, fetcher, options === null || options === void 0 ? void 0 : options.maxAgeMs, options === null || options === void 0 ? void 0 : options.maxReadAgeMs, options === null || options === void 0 ? void 0 : options.cacheValidator, options === null || options === void 0 ? void 0 : options.cacheProcessor, options === null || options === void 0 ? void 0 : options.cacheExtractor, options === null || options === void 0 ? void 0 : options.onOperation, options === null || options === void 0 ? void 0 : options.onSuccess, options === null || options === void 0 ? void 0 : options.onError, options === null || options === void 0 ? void 0 : options.useLocalStorage, handleNewChunk]);
    const refresh = useCallback((force = false) => {
        return loadChunk(inputData, undefined, force).then(() => undefined);
    }, [loadChunk, inputData]);
    const changeInput = useCallback((newInput) => {
        return loadChunk(newInput);
    }, [loadChunk]);
    const loadMore = useCallback(() => {
        if (!data.hasMore)
            return Promise.resolve(false);
        return loadChunk(inputData, data.cursor).then((chunk) => { var _a; return (_a = chunk === null || chunk === void 0 ? void 0 : chunk.hasMore) !== null && _a !== void 0 ? _a : false; });
    }, [loadChunk, data.hasMore, data.cursor, inputData]);
    useEffect(() => { refresh(); }, [enabled]);
    if ((options === null || options === void 0 ? void 0 : options.enabled) === false)
        return { status: QueryStatus.Disabled, chunks: data.chunks, hasMore: data.hasMore, cursor: data.cursor, error: undefined, refresh, changeInput, loadMore, };
    return { status, error, chunks: data.chunks, hasMore: data.hasMore, cursor: data.cursor, refresh, changeInput, loadMore };
}
const DEFAULT_UNDO_STEPS = 20;
const DEFAULT_AUTOSAVE_S = 20;
export function useWorkingCopyQuery(data, save, options) {
    const id = useId();
    const [errorOverride, setErrorOverride] = useState();
    const [statusOverride, setStatusOverride] = useState();
    const [lastUpdateHint, setLastUpdateHint] = useState(undefined);
    const [originalData, setOriginalData] = useState(data.data);
    const [workingData, setWorkingData] = useState(data.data ? { workingData: [data.data], undoIndex: 0 } : { workingData: [], undoIndex: 0 });
    const [changeCount, setChangeCount] = useState(0);
    const [lastSaved, setLastSaved] = useState(0);
    const [lastSavedData, setLastSavedData] = useState(undefined);
    function update(data, undoHint, markSaved = false) {
        if (!data)
            return;
        if (undoHint) {
            if (lastUpdateHint == undoHint) {
                setWorkingData(wd => ({ workingData: [...wd.workingData.slice(0, wd.undoIndex), data], undoIndex: wd.undoIndex }));
                return;
            }
            setLastUpdateHint(undoHint);
        }
        setWorkingData(wd => {
            var _a;
            if (wd.workingData.length == 0) // init
                return { workingData: [data], undoIndex: 0 };
            if (wd.undoIndex < wd.workingData.length - 1) // edit while in undo op state; clear redo stack
                return { workingData: [...wd.workingData.slice(0, wd.undoIndex), data], undoIndex: wd.undoIndex };
            // add to end of undo stack (but limit to maxUndoSteps)
            if (wd.workingData.length < ((_a = options === null || options === void 0 ? void 0 : options.maxUndoSteps) !== null && _a !== void 0 ? _a : DEFAULT_UNDO_STEPS))
                return { workingData: [...wd.workingData, data], undoIndex: wd.undoIndex + 1 };
            else
                return { workingData: [...wd.workingData.slice(1), data], undoIndex: wd.workingData.length - 1 };
        });
        setChangeCount(changeCount + 1);
        if (markSaved)
            setLastSaved(changeCount + 1);
    }
    const hasChanges = useMemo(() => {
        if (options === null || options === void 0 ? void 0 : options.changeDetector)
            return !!originalData && options.changeDetector(originalData, workingData.workingData[workingData.undoIndex]);
        else
            return lastSaved != changeCount;
    }, [changeCount, lastSaved, workingData, originalData, options === null || options === void 0 ? void 0 : options.changeDetector]);
    function reset(data) {
        if (data != undefined) {
            setOriginalData(data);
            setWorkingData({ workingData: [data], undoIndex: 0 });
        }
        setChangeCount(0);
        setLastSaved(0);
    }
    function undo() {
        if (workingData.undoIndex <= 0)
            return false;
        setWorkingData(wd => ({ workingData: wd.workingData, undoIndex: wd.undoIndex - 1 }));
        return true;
    }
    function redo() {
        if (workingData.undoIndex >= workingData.workingData.length - 1)
            return false;
        setWorkingData(wd => ({ workingData: wd.workingData, undoIndex: wd.undoIndex + 1 }));
        return true;
    }
    function cancel() {
        setWorkingData(originalData ? { workingData: [originalData], undoIndex: 0 } : { workingData: [], undoIndex: 0 });
        setChangeCount(0);
        setLastSaved(0);
    }
    async function storeData(d, autoSave) {
        if (lastSaved != changeCount)
            return forceStoreData(d, autoSave);
    }
    async function forceStoreData(d, autoSave) {
        try {
            setErrorOverride(undefined);
            setLastSaved(changeCount);
            const previousSave = lastSavedData;
            setLastSavedData(d);
            const update = await save(d, previousSave !== null && previousSave !== void 0 ? previousSave : originalData, autoSave);
            if (update !== undefined) {
                setOriginalData(update);
                setWorkingData({ workingData: [update], undoIndex: 0 });
                setLastSaved(0);
                setLastSavedData(update);
            }
            else
                setLastSaved(changeCount);
            setStatusOverride(undefined);
            return update !== null && update !== void 0 ? update : d;
        }
        catch (e) {
            setErrorOverride(e);
            setStatusOverride(QueryStatus.Error);
            return undefined;
        }
    }
    async function store(data, focusHint) {
        try {
            if (data !== undefined)
                return await forceStoreData(data, false);
            else if (lastSaved != changeCount)
                return await forceStoreData(workingData.workingData[workingData.undoIndex], false);
            else
                return workingData.workingData[workingData.undoIndex];
        }
        finally {
            if (focusHint)
                globalFocus(focusHint);
        }
    }
    async function refresh() {
        setStatusOverride(undefined);
        setErrorOverride(undefined);
        data.refresh();
    }
    useEffect(() => {
        if (data.status == QueryStatus.Success && data.data && data.data !== originalData) {
            setOriginalData(data.data);
            setWorkingData({ workingData: [data.data], undoIndex: 0 });
            setStatusOverride(undefined);
            setErrorOverride(undefined);
        }
    }, [data.data, data.status]);
    useEffect(() => {
        if ((options === null || options === void 0 ? void 0 : options.autoStore) == 'onUpdate' && changeCount > 0)
            storeData(workingData.workingData[workingData.undoIndex], true);
    }, [changeCount]);
    useEffect(() => {
        var _a;
        if ((options === null || options === void 0 ? void 0 : options.autoStore) == 'periodically') {
            const timeoutS = ((_a = options.autoStoreIntervalS) !== null && _a !== void 0 ? _a : DEFAULT_AUTOSAVE_S);
            const id = setTimeout(() => { storeData(workingData.workingData[workingData.undoIndex], true); }, timeoutS * 1000);
            return () => {
                clearTimeout(id);
            };
        }
    }, [options === null || options === void 0 ? void 0 : options.autoStore, changeCount, workingData.workingData, workingData.undoIndex]);
    useWillLeave(id, () => {
        if ((options === null || options === void 0 ? void 0 : options.autoStore) && (options === null || options === void 0 ? void 0 : options.autoStore) != 'never')
            storeData(workingData.workingData[workingData.undoIndex], true);
    });
    return {
        data: workingData.workingData[workingData.undoIndex],
        status: statusOverride !== null && statusOverride !== void 0 ? statusOverride : data.status,
        undoAvailable: workingData.undoIndex > 0,
        redoAvailable: workingData.undoIndex < workingData.workingData.length - 1,
        isUnsaved: hasChanges,
        error: errorOverride !== null && errorOverride !== void 0 ? errorOverride : data.error,
        refresh,
        update,
        undo,
        redo,
        cancel,
        store,
        reset
    };
}
export function useMutation(executor, cacheModifier, options) {
    const f = async (data) => {
        var _a, _b, _c, _d, _e, _f, _g, _h, _j;
        try {
            (_b = (_a = options === null || options === void 0 ? void 0 : options.onOperation) === null || _a === void 0 ? void 0 : _a[0]) === null || _b === void 0 ? void 0 : _b.call(_a);
            const result = await executor(data);
            if (cacheModifier) {
                const newValues = cacheModifier(data, result);
                for (const entry of newValues)
                    if (entry[1] === undefined)
                        removeFromQueryCache(entry[0], options === null || options === void 0 ? void 0 : options.useLocalStorage);
                for (const entry of newValues)
                    if (entry[1] !== undefined)
                        putIntoQueryCache(entry[0], entry[1], (_c = options === null || options === void 0 ? void 0 : options.maxAgeMs) !== null && _c !== void 0 ? _c : DEFAULT_CACHING_MS, options === null || options === void 0 ? void 0 : options.useLocalStorage);
            }
            (_e = (_d = options === null || options === void 0 ? void 0 : options.onOperation) === null || _d === void 0 ? void 0 : _d[1]) === null || _e === void 0 ? void 0 : _e.call(_d, true);
            (_f = options === null || options === void 0 ? void 0 : options.onSuccess) === null || _f === void 0 ? void 0 : _f.call(options, result);
            return result;
        }
        catch (e) {
            (_h = (_g = options === null || options === void 0 ? void 0 : options.onOperation) === null || _g === void 0 ? void 0 : _g[1]) === null || _h === void 0 ? void 0 : _h.call(_g, false);
            (_j = options === null || options === void 0 ? void 0 : options.onError) === null || _j === void 0 ? void 0 : _j.call(options, e);
            throw e;
        }
    };
    return f;
}
